import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  maxLength,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
} from "react-admin";
import { Grid, useMediaQuery } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { settings } from "app";
import { administrationRequests } from "api/administrationApi";
import { erasmusRequests } from "api/erasmusApi";
import { SectionTitle } from "features/SectionTitle";
import { CourseChoices, GenderOptionsId, SemestersChoices } from "utils/choices";
import AttachmentInput from "server/components/Fields/AttachmentInput";
import { InternshipIteratorItem } from "./Partials/InternshipFields";
import UniversityFields from "./Partials/UniversityFields";
import { validateInternships } from "./utils";

const NAME_FIELDS = [
  { source: "firstName", label: "Име", isRequired: true },
  { source: "lastName", label: "Фамилия", isRequired: true },
  { source: "firstNameLatin", label: "Име (латиница)", isRequired: false },
  { source: "lastNameLatin", label: "Фамилия (латиница)", isRequired: false },
];

const CreateForm = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const sectionStyle = isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox;
  const { setValue } = useFormContext();
  const structuralUnitId = useWatch({ name: "structuralUnitId" });

  return (
    <>
      <Grid
        container
        spacing={4}
        sx={sectionStyle}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Лични данни"
            paddingY={0}
          />
        </Grid>
        {NAME_FIELDS.map(({ source, label, isRequired }) => (
          <Grid
            item
            xs={12}
            md={3}
            key={source}
          >
            <TextInput
              label={label}
              source={source}
              fullWidth
              validate={isRequired ? [required(), maxLength(50)] : maxLength(50)}
            />
          </Grid>
        ))}
        <Grid
          item
          xs={12}
          md={4}
        >
          <SelectInput
            label="Пол"
            source="gender"
            choices={GenderOptionsId}
            fullWidth
            validate={required()}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="citizenship"
            reference={administrationRequests.getCitizenshipSelect()}
          >
            <SelectInput
              label="Гражданство"
              optionText="name"
              fullWidth
              validate={required()}
            />
          </ReferenceInput>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={4}
        sx={sectionStyle}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Образование"
            paddingY={0}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="cycleOfEducation"
            reference={erasmusRequests.getCycleOfEducationSelect()}
          >
            <SelectInput
              label="Тип на обучение"
              optionText="nameBg"
              fullWidth
              validate={required()}
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="structuralUnitId"
            reference={administrationRequests.getStructuralUnitsSelect()}
          >
            <SelectInput
              label="Факултет"
              fullWidth
              validate={required()}
              onChange={() => setValue("specialityId", null)}
            />
          </ReferenceInput>
        </Grid>
        {structuralUnitId ? (
          <Grid
            item
            xs={12}
            md={4}
          >
            <ReferenceInput
              source="specialityId"
              reference={administrationRequests.getSpecialitiesSelect()}
              filter={{ StructuralUnitId: structuralUnitId }}
            >
              <AutocompleteInput
                label="Специалност"
                fullWidth
                validate={required()}
                filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
              />
            </ReferenceInput>
          </Grid>
        ) : null}
        <Grid
          item
          xs={12}
          md={4}
        >
          <SelectInput
            label="Курс"
            source="course"
            choices={CourseChoices}
            fullWidth
            validate={required()}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <SelectInput
            label="Семестър"
            source="semester"
            choices={SemestersChoices}
            fullWidth
            validate={required()}
          />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={4}
        sx={sectionStyle}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Университет"
            paddingY={0}
          />
        </Grid>
        <UniversityFields />
      </Grid>

      <Grid
        container
        spacing={4}
        sx={sectionStyle}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Стажове"
            paddingY={0}
          />
        </Grid>
        <Grid
          item
          xs={12}
        >
          <ArrayInput
            label={false}
            source="internships"
            fullWidth
            defaultValue={[{}]}
            validate={validateInternships}
          >
            <SimpleFormIterator
              inline={false}
              sx={settings.style.iteratorWithoutGap}
            >
              <InternshipIteratorItem />
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={4}
        sx={sectionStyle}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Допълнителна информация"
            paddingY={0}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <BooleanInput
            label="Желае общежитие"
            source="wantsDormitory"
            defaultValue={false}
            sx={{ width: "fit-content" }}
          />
        </Grid>
        <Grid
          item
          xs={12}
        >
          <TextInput
            label="Мотивационно писмо"
            source="shortMotivationLetter"
            fullWidth
            multiline
            minRows={5}
            maxRows={5}
          />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={4}
        sx={sectionStyle}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Документи"
            paddingY={0}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <AttachmentInput
            source="cvFile"
            label="CV"
            isRequired
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <AttachmentInput
            source="motivationLetterFile"
            label="Мотивационно писмо"
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <AttachmentInput
            source="learningAgreementFile"
            label="Споразумение за обучение"
          />
        </Grid>
      </Grid>
    </>
  );
};

export default CreateForm;
